"use client";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/components/shadcn/popover";
import { ChevronRight, Share2, Check } from "lucide-react";
import { useState } from "react";
import Image from "next/image";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import { trackInteraction } from "@/app/lib/tracking/interactions";

const ShareButton = () => {
  const { product } = useProductDetail();
  const [isCopied, setIsCopied] = useState(false);

  if (!product?.share) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      if (product?.id) trackInteraction("product", product.id, "click");
    } catch (error) {
      console.error("Kopyalama başarısız:", error);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: document.title,
      text: "Bu ürünü kontrol et!",
      url: window.location.href,
    };

    if (
      navigator.share &&
      navigator.canShare &&
      navigator.canShare(shareData)
    ) {
      try {
        await navigator.share(shareData);
        if (product?.id) trackInteraction("product", product.id, "click");
      } catch {

      }
    } else {
      handleCopyLink();
    }
  };

  // API'dan gelen share items'ları kullan
  const shareLinks = product.share.dropdown.items.map((item) => {
    const getAction = () => {
      if (item.type === "copy") {
        return handleCopyLink;
      }
      
      // item.url direkt kullanılacak platform name içeriyor (whatsapp, telegram, etc.)
      return () => {
        const baseUrl = window.location.href;
        const encodedUrl = encodeURIComponent(baseUrl);

        const urlMap: Record<string, string> = {
          whatsapp: `https://wa.me/?text=${encodedUrl}`,
          telegram: `https://t.me/share/url?url=${encodedUrl}`,
          facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
          x: `https://x.com/intent/tweet?url=${encodedUrl}`,
          email: `mailto:?subject=Bu ürünü kontrol et!&body=${encodedUrl}`,
        };

        if (product?.id) trackInteraction("product", product.id, "click");
        window.open(urlMap[item.url] || item.url, "_blank");
      };
    };

    return {
      name: item.type === "copy" && isCopied ? "Kopyalandı!" : item.title,
      iconUrl: item.icon, // Icon URL'i direkt kullan
      isIconUrl: true,
      action: getAction(),
      isCopyAction: item.type === "copy",
    };
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            if (window.innerWidth <= 768) {
              e.preventDefault();
              handleShare();
            }
          }}
          className="cursor-pointer bg-white w-full border p-2 rounded-md flex items-center gap-2 font-medium text-xs transition-all duration-300 ease-in-out hover:border-black hover:shadow-lg"
        >
          <div className="w-8 h-8 flex items-center justify-center bg-[#f5f5f5] rounded-md shrink-0">
            <Share2 size={20} />
          </div>
          <p className="flex-1">{product.share.text}</p>
          <ChevronRight size={20} className="shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 hidden md:block" align="start">
        <div className="space-y-1">
          <div className="px-3 py-2 text-sm font-medium text-gray-900 border-b">
            {product.share.dropdown.text}
          </div>
          {shareLinks.map((link, index) => (
            <button
              key={index}
              onClick={link.action}
              className="cursor-pointer w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors text-left"
            >
              {link.isCopyAction && isCopied ? (
                <Check size={16} className="text-green-600" />
              ) : link.iconUrl ? (
                <Image 
                  src={link.iconUrl} 
                  alt={link.name}
                  width={16}
                  height={16}
                  className="w-4 h-4 object-contain"
                />
              ) : (
                <Share2 size={16} className="text-gray-600" />
              )}
              <span>{link.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ShareButton;
